// frontend/src/pages/SolicitudesPage.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import bodegaService from '../services/bodegaService';
import { FiCheck, FiX, FiEye } from 'react-icons/fi';
import CrearOrdenCompraOneBaseModal from '../components/CrearOrdenCompraOneBaseModal';
import Loading from '../components/Loading';
import './SolicitudesPage.css';

const SolicitudesPage = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSolicitud, setExpandedSolicitud] = useState(null);

  // Estados para filtros
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroRecurso, setFiltroRecurso] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState('');

  // Estados para el modal de crear OC
  const [showCrearOCModal, setShowCrearOCModal] = useState(false);
  const [solicitudParaAprobar, setSolicitudParaAprobar] = useState(null);
  const [presupuestoData, setPresupuestoData] = useState(null);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);

  useEffect(() => {
    cargarSolicitudes();
  }, []);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await bodegaService.getSolicitudesRecurso();
      setSolicitudes(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar las solicitudes de compra');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (solicitudId) => {
    setExpandedSolicitud(expandedSolicitud === solicitudId ? null : solicitudId);
  };

  const handleAprobar = async (solicitud) => {
    try {
      // Preparar datos de la solicitud para el modal
      setSolicitudParaAprobar(solicitud);

      // Obtener el ID del presupuesto de costo desde el primer detalle que tenga presupuesto_costo_id
      let presupuestoCostoId = 0;
      const primerDetalleConPresupuesto = solicitud.detalles?.find(d => d.recurso_data?.presupuesto_costo_id);

      if (primerDetalleConPresupuesto && primerDetalleConPresupuesto.recurso_data?.presupuesto_costo_id) {
        presupuestoCostoId = primerDetalleConPresupuesto.recurso_data.presupuesto_costo_id;
        console.log('✓ Presupuesto de costo obtenido desde detalle:', presupuestoCostoId);
      } else {
        console.log('⚠️ No se encontró presupuesto_costo_id en los detalles, el modal buscará el presupuesto activo del proyecto');
      }

      // Crear un objeto "presupuesto" con los datos del proyecto
      const presupuestoSimulado = {
        id: presupuestoCostoId, // Si es 0, el modal buscará el presupuesto activo del proyecto
        proyecto: solicitud.proyecto_data?.id || 0,
        proyecto_nombre: solicitud.proyecto_data?.nombre || 'N/A',
        version: 1,
      };

      // Transformar los detalles de la solicitud a items seleccionados
      // Ahora incluimos TODOS los campos calculados que vienen del backend
      const items = solicitud.detalles?.map(detalle => ({
        id: detalle.presupuesto_costo_recurso || detalle.recurso_data?.id || detalle.id, // ID del PresupuestoCostoRecurso
        nombre: detalle.recurso_data?.nombre || 'Recurso',
        unidad: detalle.recurso_data?.unidad_medida || 'Un',
        // Usar TODAS las cantidades que vienen calculadas del backend
        cantidad_presupuestada_costo: detalle.recurso_data?.cantidad_presupuestada_costo || detalle.recurso_data?.cantidad_presupuestada || detalle.cantidad_solicitada,
        cantidad_presupuesto: detalle.recurso_data?.cantidad_presupuesto || detalle.recurso_data?.cantidad_presupuestada || detalle.cantidad_solicitada,
        cantidad_solicitada: detalle.recurso_data?.cantidad_solicitada || 0,
        cantidad_solicitada_oc: detalle.recurso_data?.cantidad_solicitada_oc || 0,
        cantidad_salidas_bodega: detalle.recurso_data?.cantidad_salidas_bodega || 0,
        cantidad_disponible: detalle.recurso_data?.cantidad_disponible || 0,
        precio_unitario_costo: detalle.recurso_data?.precio_unitario || 0,
        recurso_referencia: detalle.recurso,
        recurso_referencia_data: {
          ...detalle.recurso_data,
          stock: false // Marcar como sin control de stock para evitar validaciones
        },
        // Usar los datos reales del área y partida del presupuesto
        area_id: detalle.recurso_data?.area_id || null,
        partida_id: detalle.recurso_data?.partida_id || null,
        area_nombre: detalle.recurso_data?.area_nombre || 'Solicitud de Compra',
        partida_nombre: detalle.recurso_data?.partida_nombre || `Evento: ${solicitud.evento_data?.nombre || 'N/A'}`,
        cantidad_dias: 1,
        // IMPORTANTE: Cantidad inicial desde la solicitud
        cantidad_inicial: detalle.cantidad || 0
      })) || [];

      setPresupuestoData(presupuestoSimulado);
      setItemsSeleccionados(items);
      setShowCrearOCModal(true);

    } catch (error) {
      console.error('Error al preparar aprobación:', error);
      toast.error('Error al preparar la orden de compra');
    }
  };

  const handleOCCreada = async () => {
    // Cuando se crea la OC exitosamente, aprobar la solicitud
    try {
      if (solicitudParaAprobar) {
        await bodegaService.aprobarSolicitudRecurso(solicitudParaAprobar.id);
        toast.success('Solicitud aprobada y Orden de Compra creada exitosamente');
        cargarSolicitudes();
      }
      setShowCrearOCModal(false);
      setSolicitudParaAprobar(null);
      setPresupuestoData(null);
      setItemsSeleccionados([]);
    } catch (error) {
      console.error('Error al aprobar solicitud:', error);
      toast.error('OC creada pero hubo un error al aprobar la solicitud');
    }
  };

  const handleCerrarModalOC = () => {
    setShowCrearOCModal(false);
    setSolicitudParaAprobar(null);
    setPresupuestoData(null);
    setItemsSeleccionados([]);
  };

  const handleRechazar = async (solicitudId) => {
    const motivo = window.prompt('Ingrese el motivo del rechazo:');
    if (!motivo) return;

    try {
      await bodegaService.rechazarSolicitudRecurso(solicitudId, { motivo });
      toast.success('Solicitud rechazada');
      cargarSolicitudes();
    } catch (error) {
      console.error('Error al rechazar solicitud:', error);
      toast.error('Error al rechazar la solicitud');
    }
  };

  const getEstadoBadgeClass = (estado) => {
    const estadoMap = {
      'pendiente': 'badge-pendiente',
      'aprobada': 'badge-aprobada',
      'rechazada': 'badge-rechazada',
      'completada': 'badge-completada'
    };
    return estadoMap[estado] || 'badge-default';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL');
  };

  // Filtrar solicitudes
  const solicitudesFiltradas = solicitudes.filter(solicitud => {
    const matchEstado = !filtroEstado || solicitud.estado === filtroEstado;
    const matchRecurso = !filtroRecurso ||
      solicitud.recurso_data?.nombre?.toLowerCase().includes(filtroRecurso.toLowerCase());
    const matchProyecto = !filtroProyecto ||
      solicitud.proyecto_data?.nombre?.toLowerCase().includes(filtroProyecto.toLowerCase());

    return matchEstado && matchRecurso && matchProyecto;
  });

  // Calcular estadísticas por estado
  const calcularEstadisticasPorEstado = () => {
    const estados = {
      pendiente: { label: 'Pendiente', count: 0, color: '#f59e0b', bgColor: '#fef3c7' },
      aprobada: { label: 'Aprobada', count: 0, color: '#3b82f6', bgColor: '#dbeafe' },
      rechazada: { label: 'Rechazada', count: 0, color: '#ef4444', bgColor: '#fee2e2' },
      completada: { label: 'Completada', count: 0, color: '#10b981', bgColor: '#d1fae5' }
    };

    solicitudesFiltradas.forEach(solicitud => {
      if (estados[solicitud.estado]) {
        estados[solicitud.estado].count++;
      }
    });

    return estados;
  };

  const estadisticas = calcularEstadisticasPorEstado();

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="solicitudes-page">
      <div className="page-header">
        <h1>Solicitudes de Compra</h1>
        <p className="page-subtitle">
          Gestione las solicitudes de recursos generadas automáticamente desde los eventos
        </p>
      </div>

      {/* Estadísticas por estado */}
      <div className="stats-grid">
        {Object.entries(estadisticas).map(([key, stat]) => (
          <div
            key={key}
            className="stat-card"
            style={{ borderLeft: `4px solid ${stat.color}` }}
          >
            <div className="stat-header">
              <span className="stat-label">{stat.label}</span>
              <span
                className={`stat-badge ${getEstadoBadgeClass(key)}`}
              >
                {stat.count}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="filters-container">
        <div className="filter-group">
          <label>Estado:</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="aprobada">Aprobada</option>
            <option value="rechazada">Rechazada</option>
            <option value="completada">Completada</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Recurso:</label>
          <input
            type="text"
            value={filtroRecurso}
            onChange={(e) => setFiltroRecurso(e.target.value)}
            placeholder="Buscar recurso..."
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Proyecto:</label>
          <input
            type="text"
            value={filtroProyecto}
            onChange={(e) => setFiltroProyecto(e.target.value)}
            placeholder="Buscar proyecto..."
            className="filter-input"
          />
        </div>

        {(filtroEstado || filtroRecurso || filtroProyecto) && (
          <button
            onClick={() => {
              setFiltroEstado('');
              setFiltroRecurso('');
              setFiltroProyecto('');
            }}
            className="btn-clear-filters"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla de solicitudes */}
      <div className="solicitudes-table-container">
        <table className="solicitudes-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Recurso</th>
              <th>Proyecto</th>
              <th>Evento</th>
              <th>Tarea</th>
              <th>Cantidad</th>
              <th>Fecha Solicitud</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {solicitudesFiltradas.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                  No se encontraron solicitudes
                </td>
              </tr>
            ) : (
              solicitudesFiltradas.map((solicitud) => (
                <React.Fragment key={solicitud.id}>
                  <tr
                    className={`solicitud-row ${expandedSolicitud === solicitud.id ? 'expanded' : ''}`}
                  >
                    <td>
                      <button
                        className="btn-expand"
                        onClick={() => toggleExpand(solicitud.id)}
                      >
                        {expandedSolicitud === solicitud.id ? '▼' : '▶'}
                      </button>
                    </td>
                    <td>
                      <div className="recurso-info">
                        <strong>{solicitud.recurso_data?.nombre || '-'}</strong>
                        <small>{solicitud.recurso_data?.codigo || ''}</small>
                      </div>
                    </td>
                    <td>{solicitud.proyecto_data?.nombre || '-'}</td>
                    <td>{solicitud.evento_data?.nombre || '-'}</td>
                    <td>{solicitud.tarea_data?.nombre || '-'}</td>
                    <td>
                      <span className="cantidad-badge">
                        {solicitud.cantidad_solicitada} {solicitud.recurso_data?.unidad_medida || ''}
                      </span>
                    </td>
                    <td>{formatDate(solicitud.created_at)}</td>
                    <td>
                      <span className={`estado-badge ${getEstadoBadgeClass(solicitud.estado)}`}>
                        {solicitud.estado}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {solicitud.estado === 'pendiente' && (
                          <>
                            <button
                              className="btn-action btn-aprobar"
                              onClick={() => handleAprobar(solicitud)}
                              title="Aprobar solicitud"
                            >
                              <FiCheck />
                            </button>
                            <button
                              className="btn-action btn-rechazar"
                              onClick={() => handleRechazar(solicitud.id)}
                              title="Rechazar solicitud"
                            >
                              <FiX />
                            </button>
                          </>
                        )}
                        <button
                          className="btn-action btn-ver"
                          onClick={() => toggleExpand(solicitud.id)}
                          title="Ver detalles"
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedSolicitud === solicitud.id && (
                    <tr className="expanded-row">
                      <td colSpan="9">
                        <div className="expanded-content">
                          <div className="details-grid">
                            <div className="detail-section">
                              <h4>Información del Recurso</h4>
                              <div className="detail-item">
                                <label>Código:</label>
                                <span>{solicitud.recurso_data?.codigo || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Nombre:</label>
                                <span>{solicitud.recurso_data?.nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Tipo:</label>
                                <span>{solicitud.recurso_data?.tipo_recurso_data?.nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Unidad de Medida:</label>
                                <span>{solicitud.recurso_data?.unidad_medida || '-'}</span>
                              </div>
                            </div>

                            <div className="detail-section">
                              <h4>Información de la Solicitud</h4>
                              <div className="detail-item">
                                <label>Cantidad Solicitada:</label>
                                <span>{solicitud.cantidad_solicitada} {solicitud.recurso_data?.unidad_medida || ''}</span>
                              </div>
                              <div className="detail-item">
                                <label>Fecha de Creación:</label>
                                <span>{formatDate(solicitud.created_at)}</span>
                              </div>
                              <div className="detail-item">
                                <label>Estado:</label>
                                <span className={`estado-badge ${getEstadoBadgeClass(solicitud.estado)}`}>
                                  {solicitud.estado}
                                </span>
                              </div>
                              {solicitud.motivo_rechazo && (
                                <div className="detail-item">
                                  <label>Motivo de Rechazo:</label>
                                  <span className="text-danger">{solicitud.motivo_rechazo}</span>
                                </div>
                              )}
                            </div>

                            <div className="detail-section">
                              <h4>Contexto del Proyecto</h4>
                              <div className="detail-item">
                                <label>Proyecto:</label>
                                <span>{solicitud.proyecto_data?.nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Cliente:</label>
                                <span>{solicitud.proyecto_data?.cliente_data?.nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Evento:</label>
                                <span>{solicitud.evento_data?.nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Tarea:</label>
                                <span>{solicitud.tarea_data?.nombre || '-'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para crear Orden de Compra */}
      {showCrearOCModal && presupuestoData && itemsSeleccionados.length > 0 && (
        <CrearOrdenCompraOneBaseModal
          presupuesto={presupuestoData}
          itemsSeleccionados={itemsSeleccionados}
          onClose={handleCerrarModalOC}
          onCreated={handleOCCreada}
        />
      )}
    </div>
  );
};

export default SolicitudesPage;
